import { type ReactNode } from 'react';
import classNames from 'classnames';

import {
    ExperienceGuarantee,
    Image,
    LayoutVariant,
    ShieldCheckBlueIcon,
    ShieldCheckGoldIcon,
} from '@jsmdg/yoshi';
import { useExperienceGuaranteeConfig } from '../../hooks';
import styles from './ExperienceGuaranteeWrapper.module.scss';

const renderBulletItems = (bulletItems: Array<{ text: string }>, isMydays: boolean): ReactNode => {
    const icon = isMydays ? <ShieldCheckGoldIcon /> : <ShieldCheckBlueIcon />;

    return bulletItems.map(item => {
        const parts = item.text.split(': ');
        const title = parts[0];
        const description = parts.slice(1).join(': ');

        return (
            <ExperienceGuarantee.BulletItem
                key={item.text}
                icon={icon}
                className={styles.fontSize17}
            >
                <span className="fw-semibold">{title}</span>
                {description && <>: {description}</>}
            </ExperienceGuarantee.BulletItem>
        );
    });
};

const ExperienceGuaranteeWrapper = (): JSX.Element => {
    const config = useExperienceGuaranteeConfig();
    const { bulletItems, headline, isMydays, layoutVariant } = config;

    if (layoutVariant === LayoutVariant.WithImage && 'imageSrc' in config) {
        const { description, imageAlt, imageHeight, imageSrc, imageWidth } = config;

        return (
            <ExperienceGuarantee layoutVariant={layoutVariant} className="gap-2x">
                <ExperienceGuarantee.Column className="py-2x pl-2x">
                    <ExperienceGuarantee.Headline>{headline}</ExperienceGuarantee.Headline>
                    {description && (
                        <ExperienceGuarantee.Description className={styles.fontSize17}>
                            {description}
                        </ExperienceGuarantee.Description>
                    )}
                    <ExperienceGuarantee.Bullets className={classNames('pr-5x', styles.fontSize17)}>
                        {renderBulletItems(bulletItems, isMydays)}
                    </ExperienceGuarantee.Bullets>
                </ExperienceGuarantee.Column>
                <ExperienceGuarantee.Column className={styles.columnWithMaxHeight}>
                    <ExperienceGuarantee.Image>
                        <Image
                            src={imageSrc}
                            alt={imageAlt}
                            width={imageWidth}
                            height={imageHeight}
                        />
                    </ExperienceGuarantee.Image>
                </ExperienceGuarantee.Column>
            </ExperienceGuarantee>
        );
    }

    const { bulletsAccordionTitle, collapsibleBullets } = config;

    return (
        <ExperienceGuarantee layoutVariant={layoutVariant} className="mt-2x">
            <ExperienceGuarantee.Headline>{headline}</ExperienceGuarantee.Headline>
            <ExperienceGuarantee.Bullets
                collapsible={collapsibleBullets}
                title={bulletsAccordionTitle}
                className={classNames('pr-0', styles.fontSize17)}
            >
                {renderBulletItems(bulletItems, isMydays)}
            </ExperienceGuarantee.Bullets>
        </ExperienceGuarantee>
    );
};

export { ExperienceGuaranteeWrapper };
